import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { useRole } from 'model/Role';
import { Partner } from 'model/Partner';
import { Ledger } from 'model/Ledger';
import { Report } from 'model/Report';
import { generalStyle } from 'helper/Style';
import { convertDateCode, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import SelectDate from 'component/part/SelectDate';
import H2 from 'component/part/H2';

type MemberUser = User & { assigned: boolean, otherReport: Report | null };
type MemberPartner = Partner & { assigned: boolean, otherReport: Report | null };

const style = {
  formArea: {
    ...generalStyle.formRow,
    alignItems: 'top',
    gap: '4rem',
    fontSize: '1.6rem',
  },
  memberRow: (disabled: boolean) => ({
    ...generalStyle.listRow,
    ['& > div']: {
      borderLeft: 'solid 0.1rem #ddd',
      borderBottom: 'solid 0.1rem #ddd',
      padding: '1rem',
      ...(disabled ? { backgroundColor: '#eeeeee' } : {}),
    },
    ['&:nth-of-type(1) > div']: {
      borderTop: 'solid 0.1rem #ddd',
      backgroundColor: '#eeeeee',
    },
    ['& > div:nth-last-of-type(1)']: {
      borderRight: 'solid 0.1rem #ddd',
    },
  }),
  reset: {
    display: 'block',
    width: '2rem',
    height: '2rem',
  },
};

const ArrangementSetup: React.FC = () => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string, date: string }>();
  const ledgerId = parseInt(urlParams.id || '');
  const [date, setDate] = useState<Date>(tomorrow);
  const [users, setUsers] = useState<MemberUser[]>([]);
  const [partners, setPartners] = useState<MemberPartner[]>([]);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [ledger, setLedger] = useState<Ledger>();
  const [dateReports, setDateReports] = useState<{ [dateCode: number]: Report[] }>({});
  const dateCode = convertDateCode(date);
  const assigned = !!(dateReports[dateCode] || []).find((report) => report.ledgerId === ledgerId);

  const assignUser = (user: MemberUser, assign = true) => {
    const targetUser = users.find(({ id }) => id === user.id);
    if (targetUser) {
      targetUser.assigned = assign;
      setUsers([...users]);
    }
  };

  const assignPartner = (partner: MemberPartner, assign = true) => {
    const targetPartner = partners.find(({ id }) => id === partner.id);
    if (targetPartner) {
      targetPartner.assigned = assign;
      setPartners([...partners]);
    }
  };

  const onSubmit = async (execute: boolean) => {
    if (assigned || !ledger) {
      return false;
    }
    if (!execute) {
      return true;
    }

    const memberUserIds = users.flatMap((user) => user.assigned ? user.id : []);
    const memberPartnerIds = partners.flatMap((partner) => partner.assigned ? partner.id : []);
    date.setHours(8);
    date.setMinutes(0);
    const datetime = Math.floor(date.getTime() / 1000);

    const result = await api.ledger.assign(ledger.id, datetime, memberUserIds, memberPartnerIds);

    return result.ok && !!result.data;
  };

  useEffect(() => {
    (async () => {
      const roles = await getRoleListAsync();
      const assignTargetRoleIds = roles.flatMap((role) => role.permissions.workAssign ? role.id : []);
      const [usersResponse, partnersResponse, ledgersResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.partner.getsByOwn(),
        api.ledger.getsByOwn(),
      ]);
      const targetUsers = usersResponse.flatMap(
        (user) => assignTargetRoleIds.includes(user.roleId) ? { ...user, assigned: false, otherReport: null } : []
      );
      const targetPartners = partnersResponse.map(
        (partner) => ({ ...partner, assigned: false, otherReport: null })
      );
      setUsers(targetUsers);
      setPartners(targetPartners);
      setLedgers(ledgersResponse);

      const initialDateCode = parseInt(urlParams.date || '');
      if (initialDateCode) {
        const year = Math.floor(initialDateCode / 10000);
        const month = Math.floor((initialDateCode - year * 10000) / 100);
        const day = initialDateCode % 100;
        const initialDate = new Date(year, month - 1, day, 9);
        setDate(initialDate);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!(dateCode in dateReports)) {
        const reportsResponse = await api.report.getsByOwn({ dateCode, with: ['ledger', 'member'] });
        dateReports[dateCode] = reportsResponse;
      }
      setDateReports({ ...dateReports });
      window.history.pushState(null, '', `/arrangement/${ledgerId}/${dateCode}`);
    })();
  }, [date]);

  useEffect(() => {
    setLedger(ledgers.find(({ id }) => id === ledgerId));
  }, [ledgerId, ledgers]);

  useEffect(() => {
    const reports = dateReports[dateCode] || [];
    [users, partners].forEach((members, isPartner) => {
      members.forEach((member) => {
        const assignedReport = reports.find((report) => {
          const reportMembers = isPartner ? report.memberPartners : report.memberUsers;

          return reportMembers?.find(({ memberId }) => memberId === member.id);
        });
        member.assigned = !!assignedReport && assignedReport.ledgerId === ledger?.id;
        member.otherReport = (assignedReport?.ledgerId !== ledger?.id && assignedReport) || null;
      });
    });
    setUsers([...users]);
    setPartners([...partners]);
  }, [ledger, dateReports]);

  return <Layout>
    <BackLink link={`/ledger/list/${convertDateCode(date)}`} label='台帳一覧'></BackLink>
    <H2>人員手配</H2>
    <div css={generalStyle.form}>
      <div css={style.formArea}>
        <FormContent label='現場名略称'>
          <div css={generalStyle.item()}>{ledger?.shortName || ''}</div>
        </FormContent>
        <FormContent label='作業日'>
          <SelectDate
            onChange={(value) => value && setDate(value)}
            selected={date}
            minDate={today}
          />
        </FormContent>
      </div>
      <div css={style.formArea}>
        <FormContent label='作業員選択'>
          <div css={generalStyle.list(3)}>
            <label css={style.memberRow(false)}>
              <div></div>
              <div>スタッフ</div>
              <div>配置状況</div>
            </label>
            {users.map((user, index) => (
              <label css={style.memberRow(!!user.otherReport)} key={index}>
                <div>
                  <input
                    type='checkbox'
                    checked={user.assigned}
                    onChange={() => assignUser(user, !user.assigned)}
                    disabled={assigned || !!user.otherReport}
                  />
                </div>
                <div>{convertUserName(user)}</div>
                <div>
                  {
                    user.otherReport
                      ? `${user.otherReport.ledger?.shortName}に配置済み` :
                    !assigned
                      ? '配置可能' : ''
                  }
                </div>
              </label>
            ))}
          </div>
        </FormContent>
        <FormContent label='協力業者選択'>
          <div css={generalStyle.list(3)}>
            <label css={style.memberRow(false)}>
              <div></div>
              <div>業者</div>
              <div>配置状況</div>
            </label>
            {partners.map((partner, index) => (
              <label css={style.memberRow(false)} key={index}>
                <div>
                  <input
                    type='checkbox'
                    checked={partner.assigned}
                    onChange={() => assignPartner(partner, !partner.assigned)}
                  />
                </div>
                <div>{partner.name}</div>
                <div>
                  {
                    partner.otherReport
                      ? `${partner.otherReport.ledger?.shortName}に配置済み`
                      : ''
                  }
                </div>
              </label>
            ))}
          </div>
        </FormContent>
      </div>
      <OperationTrigger
        onConfirm={() => onSubmit(false)}
        onSubmit={() => onSubmit(true)}
        onOk={() => navigate(`/ledger/list/${convertDateCode(date)}`)}
        buttonText='確定'
        objectType='arrangement'
        operationType='create'
      />
    </div>
  </Layout>;
};

export default ArrangementSetup;
